import "@fontsource-variable/inter";
import "./index.css";

import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { Navigate, RouterProvider, createBrowserRouter } from "react-router";

import { RootLayout } from "@/components/layout/root-layout";
import { ThemeProvider } from "@/components/theme-provider";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AdminChallengeBasics } from "@/routes/admin/challenge/basics";
import { AdminChallengeLayout } from "@/routes/admin/challenge/layout";
import { AdminChallengeLimits } from "@/routes/admin/challenge/limits";
import { AdminChallengeMaze } from "@/routes/admin/challenge/maze";
import { AdminChallengeReport } from "@/routes/admin/challenge/report";
import { AdminChallengeScoring } from "@/routes/admin/challenge/scoring";
import { AdminChallengeAttemptsRoute } from "@/routes/admin/challenge/attempts";
import { AdminChallengeSubmissionsRoute } from "@/routes/admin/challenge/submissions";
import { AdminMazeDetailRoute } from "@/routes/admin/mazes/maze-detail";
import { AdminMazesRoute } from "@/routes/admin/mazes/mazes";
import { AdminChallengesRoute } from "@/routes/admin/challenges";
import { AdminGroupsRoute } from "@/routes/admin/groups";
import { AdminReportsRoute } from "@/routes/admin/reports";
import { AdminSlackMessagesRoute, AdminSlackRoute, AdminSlackSyncRoute } from "@/routes/admin/slack";
import { AdminLayout } from "@/routes/admin/layout";
import { AdminStudentsRoute } from "@/routes/admin/students";
import { AdminTermsRoute } from "@/routes/admin/terms";
import { AttemptDetailRoute } from "@/routes/attempt-detail";
import { ChallengeDetailRoute } from "@/routes/challenge-detail";
import { ChallengeSubmitRoute } from "@/legacy/challenge-submit";
import { GettingStartedRoute } from "@/routes/getting-started";
import { GuidelinesRoute } from "@/routes/guidelines/index";
import { LocalGuideRoute } from "@/routes/guidelines/local";
import { SdkReferenceRoute } from "@/routes/sdk-reference";
import { HomeRoute } from "@/routes/home";
import { NotFoundRoute } from "@/routes/not-found";
import { SubmissionDetailRoute } from "@/routes/submission-detail";
import { TeamRoute } from "@/routes/team";

/**
 * `basename` comes from Vite's `base`, so the same source builds correctly both
 * for GitHub Pages under /vis_nav_player/ and for serving from a domain root.
 */
const router = createBrowserRouter(
  [
    {
      element: <RootLayout />,
      children: [
        { index: true, element: <GettingStartedRoute /> },
        { path: "challenges", element: <HomeRoute /> },
        { path: "sdk", element: <SdkReferenceRoute /> },
        { path: "guidelines", element: <GuidelinesRoute /> },
        { path: "guidelines/local", element: <LocalGuideRoute /> },
        { path: "team", element: <TeamRoute /> },
        { path: "challenges/:id", element: <ChallengeDetailRoute /> },
        { path: "challenges/:id/submit", element: <ChallengeSubmitRoute /> },
        {
          path: "challenges/:id/submissions/:submissionId",
          element: <SubmissionDetailRoute />,
        },
        // A server-simulated run's page. The SDK opens it when a session starts; a
        // scored one's submission route redirects here.
        {
          path: "challenges/:id/attempts/:sessionId",
          element: <AttemptDetailRoute />,
        },
        {
          path: "admin",
          element: <AdminLayout />,
          children: [
            { index: true, element: <Navigate to="challenges" replace /> },
            { path: "challenges", element: <AdminChallengesRoute /> },
            // One challenge, in sections down a side nav; each section saves itself.
            {
              path: "challenges/:id",
              element: <AdminChallengeLayout />,
              children: [
                { index: true, element: <AdminChallengeBasics /> },
                { path: "maze", element: <AdminChallengeMaze /> },
                // The old setup page's address, kept for bookmarks.
                { path: "setup", element: <Navigate to="../maze" replace /> },
                { path: "scoring", element: <AdminChallengeScoring /> },
                { path: "limits", element: <AdminChallengeLimits /> },
                { path: "report", element: <AdminChallengeReport /> },
                { path: "submissions", element: <AdminChallengeSubmissionsRoute /> },
                { path: "attempts", element: <AdminChallengeAttemptsRoute /> },
                { path: "sessions", element: <Navigate to="../attempts" replace /> },
              ],
            },
            // `keys` kept as a redirect: the page is about the roster and the key
            // is one of its columns, but the old path may be bookmarked.
            { path: "mazes", element: <AdminMazesRoute /> },
            { path: "mazes/:id", element: <AdminMazeDetailRoute /> },
            { path: "keys", element: <Navigate to="/admin/students" replace /> },
            { path: "students", element: <AdminStudentsRoute /> },
            { path: "groups", element: <AdminGroupsRoute /> },
            { path: "reports", element: <AdminReportsRoute /> },
            {
              path: "slack",
              element: <AdminSlackRoute />,
              children: [
                { index: true, element: <AdminSlackSyncRoute /> },
                { path: "messages", element: <AdminSlackMessagesRoute /> },
              ],
            },
            { path: "terms", element: <AdminTermsRoute /> },
          ],
        },
        { path: "*", element: <NotFoundRoute /> },
      ],
    },
  ],
  { basename: import.meta.env.BASE_URL.replace(/\/$/, "") || undefined },
);

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <ThemeProvider defaultTheme="system" storageKey="vis_nav_theme">
      <TooltipProvider>
        <RouterProvider router={router} />
      </TooltipProvider>
    </ThemeProvider>
  </StrictMode>,
);
